/**
 * Drivers hub page.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
    Activity,
    BarChart3,
    ChevronRight,
    Gauge,
    Search,
    Shield,
    Sparkles,
    Trophy,
    X,
    Zap,
} from "lucide-react";
import { api, useFetch } from "../lib/api";
import { EmptyState } from "../components/atoms";
import { buildDriverEnrichment, getDriverImagePath } from "./driversConfig";
import "./drivers.css";

const FILTERS = [
    { id: "active", label: "Active" },
    { id: "champion", label: "Champions" },
    { id: "legend", label: "Legends" },
];

const cardVariants = {
    hidden: { opacity: 0, y: 24, scale: 0.98 },
    show: (delay = 0) => ({
        opacity: 1,
        y: 0,
        scale: 1,
        transition: { duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] },
    }),
};

const topCardMotion = {
    1: { hidden: { opacity: 0, y: -34, scale: 0.96 }, show: { opacity: 1, y: 0, scale: 1 } },
    2: { hidden: { opacity: 0, x: -42, scale: 0.96 }, show: { opacity: 1, x: 0, scale: 1 } },
    3: { hidden: { opacity: 0, x: 42, scale: 0.96 }, show: { opacity: 1, x: 0, scale: 1 } },
};

export default function Drivers() {
    const { data, loading, error } = useFetch(() => api.driversAll(), []);
    const [filter, setFilter] = useState("active");
    const [q, setQ] = useState("");
    const [selectedDriver, setSelectedDriver] = useState(null);
    const triggerRef = useRef(null);

    const drivers = useMemo(() => (data?.drivers || []).map(normalizeDriver), [data]);

    const visible = useMemo(() => {
        const normalizedQuery = q.trim().toLowerCase();
        const filtered = drivers.filter((driver) => {
            if (filter === "active" && driver.status !== "active") return false;
            if (filter === "champion" && (driver.championships || 0) <= 0) return false;
            if (filter === "legend" && driver.status !== "legend") return false;
            if (!normalizedQuery) return true;

            return [
                driver.name,
                driver.firstName,
                driver.lastName,
                driver.code,
                driver.teamName,
                driver.country,
                driver.status,
            ]
                .filter(Boolean)
                .some((value) => String(value).toLowerCase().includes(normalizedQuery));
        });

        return filtered.sort(sortDriversForLeaderboard);
    }, [drivers, filter, q]);

    const topDrivers = visible.slice(0, 3);
    const remainingDrivers = visible.slice(3);

    const openProfile = (driver, event) => {
        triggerRef.current = event?.currentTarget || document.activeElement;
        setSelectedDriver(driver);
    };

    const closeProfile = () => {
        setSelectedDriver(null);
        window.setTimeout(() => triggerRef.current?.focus?.(), 0);
    };

    return (
        <div className="drivers-page" data-testid="drivers-page">
            <div className="drivers-grid-bg" aria-hidden />
            <section className="drivers-shell">
                <motion.div
                    className="drivers-header"
                    initial={{ opacity: 0, y: 22 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                >
                    <div>
                        <span className="label-eyebrow text-f1-red">DRIVERS HUB</span>
                        <h1 className="drivers-title">Driver command wall</h1>
                        <p className="drivers-subtitle">
                            Current grid, champions, and legends rendered as a live ranking surface.
                        </p>
                    </div>
                </motion.div>

                <DriverFilters filter={filter} setFilter={setFilter} q={q} setQ={setQ} />

                {error ? (
                    <EmptyState title="Drivers unavailable" message="The Drivers API did not respond." icon={Shield} />
                ) : loading ? (
                    <DriversSkeleton />
                ) : visible.length === 0 ? (
                    <EmptyState title="No drivers found" message="Try another search or filter." icon={Search} />
                ) : (
                    <>
                        <DriversHeroPodium drivers={topDrivers} onOpenProfile={openProfile} />
                        {remainingDrivers.length > 0 && (
                            <section className="drivers-list-section">
                                <div className="drivers-section-header">
                                    <div>
                                        <span className="label-eyebrow text-zinc-500">FULL FIELD</span>
                                        <h2>Chasing pack</h2>
                                    </div>
                                    <span className="drivers-field-count">
                                        {remainingDrivers.length} DRIVERS
                                    </span>
                                </div>
                                <div className="drivers-mini-grid">
                                    {remainingDrivers.map((driver, index) => (
                                        <DriverMiniCard
                                            key={driver.id}
                                            driver={driver}
                                            index={index}
                                            onOpenProfile={openProfile}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}
                    </>
                )}
            </section>

            <DriverProfileModal driver={selectedDriver} onClose={closeProfile} />
        </div>
    );
}

function DriverFilters({ filter, setFilter, q, setQ }) {
    return (
        <motion.div
            className="drivers-controls"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
            <div className="drivers-filter-tabs" role="group" aria-label="Driver filters">
                {FILTERS.map((item) => (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => setFilter(item.id)}
                        data-testid={`driver-filter-${item.id}`}
                        className={`drivers-filter-pill ${filter === item.id ? "is-active" : ""}`}
                    >
                        {item.label}
                    </button>
                ))}
            </div>
            <label className="drivers-search">
                <Search className="h-4 w-4" />
                <span className="sr-only">Search drivers</span>
                <input
                    type="text"
                    value={q}
                    onChange={(event) => setQ(event.target.value)}
                    placeholder="Search drivers, teams..."
                    data-testid="driver-search"
                />
            </label>
        </motion.div>
    );
}

function DriversHeroPodium({ drivers, onOpenProfile }) {
    if (!drivers.length) return null;

    const rankOrder =
        drivers.length === 3
            ? [
                  { driver: drivers[1], podiumSlot: 2 },
                  { driver: drivers[0], podiumSlot: 1 },
                  { driver: drivers[2], podiumSlot: 3 },
              ]
            : drivers.map((driver, index) => ({ driver, podiumSlot: index + 1 }));

    return (
        <section className={`drivers-podium drivers-podium-count-${drivers.length}`}>
            {drivers.length === 3 && (
                <motion.svg
                    className="drivers-podium-lines"
                    viewBox="0 0 1000 560"
                    preserveAspectRatio="none"
                    aria-hidden
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.65, duration: 0.45 }}
                >
                    <motion.path
                        d="M170 420 L500 78 L830 420 L170 420"
                        fill="none"
                        stroke="url(#driverPodiumStroke)"
                        strokeWidth="2"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ delay: 0.7, duration: 1.15, ease: [0.22, 1, 0.36, 1] }}
                    />
                    <defs>
                        <linearGradient id="driverPodiumStroke" x1="0" x2="1" y1="0" y2="1">
                            <stop offset="0%" stopColor="#E10600" stopOpacity="0.12" />
                            <stop offset="48%" stopColor="#FFFFFF" stopOpacity="0.55" />
                            <stop offset="100%" stopColor="#E10600" stopOpacity="0.45" />
                        </linearGradient>
                    </defs>
                </motion.svg>
            )}

            <div className="drivers-podium-grid">
                {rankOrder.map(({ driver, podiumSlot }) => (
                    <DriverFeatureCard
                        key={driver.id}
                        driver={driver}
                        displayRank={podiumSlot}
                        onOpenProfile={onOpenProfile}
                    />
                ))}
            </div>
        </section>
    );
}

function DriverFeatureCard({ driver, displayRank, onOpenProfile }) {
    const isLeader = displayRank === 1;
    const motionConfig = topCardMotion[displayRank] || cardVariants;
    const openFromCard = (event) => onOpenProfile(driver, event);
    const handleCardKeyDown = (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        onOpenProfile(driver, event);
    };

    return (
        <motion.article
            className={`driver-feature-card rank-${displayRank} ${isLeader ? "is-leader" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={`Open ${driver.name} profile`}
            data-testid={`driver-feature-card-${driver.id}`}
            custom={displayRank * 0.12}
            variants={motionConfig}
            initial="hidden"
            animate="show"
            transition={{ duration: 0.65, delay: isLeader ? 0.18 : 0.32, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6, scale: isLeader ? 1.018 : 1.014 }}
            onClick={openFromCard}
            onKeyDown={handleCardKeyDown}
            style={{ "--team-color": driver.teamColor }}
        >
            <div className="driver-card-scan" aria-hidden />
            <div className="driver-card-angle" aria-hidden />
            <div className="driver-feature-rank">#{driver.rankLabel}</div>
            <DriverPortrait driver={driver} className="driver-feature-portrait" eager={isLeader} />
            <div className="driver-feature-content">
                <div className="driver-feature-meta">
                    <span>{driver.country || "F1"}</span>
                    <span>{driver.teamName || "Independent"}</span>
                </div>
                <h2>{driver.name}</h2>
                <div className="driver-feature-code">{driver.code}</div>
                <div className="driver-feature-stats">
                    <StatChip label="Points" value={driver.points ?? "—"} accent />
                    <StatChip label="Form" value={driver.formIndex ? `${driver.formIndex}` : "N/A"} />
                    <StatChip label="Quali" value={driver.qualifyingRank ? `P${driver.qualifyingRank}` : "N/A"} />
                    <StatChip label="Pace" value={driver.racePaceRank ? `P${driver.racePaceRank}` : "N/A"} />
                </div>
                <div className="driver-feature-footer">
                    <DriverSparkline values={driver.sparklineValues} color={driver.teamColor} />
                    <span
                        className="driver-profile-button"
                        aria-hidden="true"
                    >
                        Open Profile
                        <ChevronRight className="h-4 w-4" />
                    </span>
                </div>
            </div>
        </motion.article>
    );
}

function DriverMiniCard({ driver, index, onOpenProfile }) {
    return (
        <motion.article
            className="driver-mini-card"
            variants={cardVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            custom={Math.min(index * 0.035, 0.35)}
            style={{ "--team-color": driver.teamColor }}
        >
            <button
                type="button"
                className="driver-mini-button"
                onClick={(event) => onOpenProfile(driver, event)}
                aria-label={`Open ${driver.name} profile`}
                data-testid={`driver-card-${driver.id}`}
            >
                <div className="driver-mini-rank">#{driver.rankLabel}</div>
                <DriverPortrait driver={driver} className="driver-mini-portrait" />
                <div className="driver-mini-info">
                    <div className="driver-mini-topline">
                        <span>{driver.code}</span>
                        <span>{driver.points ?? "—"} PTS</span>
                    </div>
                    <h3>{driver.name}</h3>
                    <p>{driver.teamName || "No team listed"}</p>
                    <div className="driver-mini-stats">
                        <StatChip label="Form" value={driver.formIndex ? driver.formIndex : "N/A"} />
                        <StatChip label="Wins" value={driver.wins ?? driver.careerWins ?? "—"} />
                        <StatChip label="Podiums" value={driver.podiums ?? driver.careerPodiums ?? "—"} />
                    </div>
                </div>
                <DriverSparkline values={driver.sparklineValues} color={driver.teamColor} compact />
            </button>
        </motion.article>
    );
}

function DriverProfileModal({ driver, onClose }) {
    const dialogRef = useRef(null);

    useEffect(() => {
        if (!driver) return undefined;

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const focusTimer = window.setTimeout(() => {
            const first = dialogRef.current?.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            first?.focus?.();
        }, 50);

        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
            }
            if (event.key !== "Tab" || !dialogRef.current) return;

            const focusable = Array.from(
                dialogRef.current.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
                )
            ).filter((element) => !element.disabled && element.offsetParent !== null);

            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => {
            window.clearTimeout(focusTimer);
            document.body.style.overflow = previousOverflow;
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [driver, onClose]);

    const enrichment = useMemo(() => (driver ? buildDriverEnrichment(driver) : null), [driver]);

    const modal = (
        <AnimatePresence>
            {driver && (
                <motion.div
                    className="driver-modal-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    onMouseDown={(event) => {
                        if (event.target === event.currentTarget) onClose();
                    }}
                >
                    <motion.section
                        ref={dialogRef}
                        className="driver-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="driver-profile-title"
                        initial={{ opacity: 0, scale: 0.96, y: 18 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.98, y: 12 }}
                        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                        style={{ "--team-color": driver.teamColor }}
                    >
                        <button type="button" className="driver-modal-close" onClick={onClose} aria-label="Close driver profile">
                            <X className="h-5 w-5" />
                        </button>

                        <div className="driver-modal-hero">
                            <motion.div
                                className="driver-modal-portrait-wrap"
                                initial={{ opacity: 0, x: -24 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                            >
                                <DriverPortrait driver={driver} className="driver-modal-portrait" eager />
                            </motion.div>
                            <div className="driver-modal-identity">
                                <span className="label-eyebrow text-f1-red">DRIVER PROFILE</span>
                                <h2 id="driver-profile-title">{driver.name}</h2>
                                <div className="driver-modal-meta">
                                    <span>#{driver.rankLabel}</span>
                                    <span>{driver.teamName || "No team listed"}</span>
                                    <span>{driver.country || "Nationality N/A"}</span>
                                    <span>No. {driver.number || "N/A"}</span>
                                </div>
                                <p>{enrichment.summary}</p>
                                <div className="driver-modal-ctas">
                                    <Link to={`/drivers/${driver.id}`} className="driver-route-link" onClick={onClose}>
                                        View full route
                                        <ChevronRight className="h-4 w-4" />
                                    </Link>
                                </div>
                            </div>
                            <div className="driver-modal-score">
                                <span>FORM INDEX</span>
                                <strong>{driver.formIndex || "N/A"}</strong>
                                <small>{driver.points ?? "—"} season points</small>
                            </div>
                        </div>

                        <div className="driver-modal-body">
                            <section className="driver-modal-panel">
                                <PanelTitle icon={Trophy} title="Performance stats" />
                                <div className="driver-stat-grid">
                                    <ModalStat label="Championship pos" value={driver.rank ? `P${driver.rank}` : "N/A"} />
                                    <ModalStat label="Points" value={driver.points ?? "—"} />
                                    <ModalStat label="Wins" value={driver.wins ?? driver.careerWins ?? "—"} />
                                    <ModalStat label="Podiums" value={driver.podiums ?? driver.careerPodiums ?? "—"} />
                                    <ModalStat label="Poles" value={driver.poles ?? driver.careerPoles ?? "—"} />
                                    <ModalStat label="Fastest laps" value={driver.fastestLaps ?? "N/A"} />
                                    <ModalStat label="DNFs" value={driver.dnfs ?? "N/A"} />
                                    <ModalStat label="Avg finish" value={driver.averageFinish || averageFinish(driver.recentForm)} />
                                    <ModalStat label="Quali rank" value={driver.qualifyingRank ? `P${driver.qualifyingRank}` : "N/A"} />
                                    <ModalStat label="Race pace" value={driver.racePaceRank ? `P${driver.racePaceRank}` : "N/A"} />
                                    <ModalStat label="Form index" value={driver.formIndex || "N/A"} />
                                    <ModalStat label="Titles" value={driver.championships ?? 0} />
                                </div>
                            </section>

                            <section className="driver-modal-panel">
                                <PanelTitle icon={Activity} title="Analysis" />
                                <div className="driver-analysis-list">
                                    {enrichment.analysis.map((item, index) => (
                                        <motion.div
                                            key={item.label}
                                            className="driver-analysis-item"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: 0.16 + index * 0.045, duration: 0.35 }}
                                        >
                                            <span>{item.label}</span>
                                            <p>{item.body}</p>
                                        </motion.div>
                                    ))}
                                </div>
                            </section>

                            <section className="driver-modal-panel">
                                <PanelTitle icon={Zap} title="Visual analytics" />
                                <div className="driver-chart-block">
                                    <DriverSparkline values={driver.sparklineValues} color={driver.teamColor} large />
                                    <StatBar label="Form" value={driver.formIndex || 0} color={driver.teamColor} />
                                    <StatBar label="Career wins" value={percentile(driver.careerWins, 105)} color={driver.teamColor} />
                                    <StatBar label="Podiums" value={percentile(driver.careerPodiums, 204)} color={driver.teamColor} />
                                    <StatBar label="Season points" value={percentile(driver.points, 160)} color={driver.teamColor} />
                                </div>
                            </section>

                            <section className="driver-modal-panel">
                                <PanelTitle icon={Sparkles} title="Career overview" />
                                <div className="driver-overview-split">
                                    <div>
                                        <span>Strengths</span>
                                        <ul>
                                            {enrichment.strengths.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div>
                                        <span>Development notes</span>
                                        <ul>
                                            {enrichment.weaknesses.map((item) => (
                                                <li key={item}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </motion.section>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return createPortal(modal, document.body);
}

function DriverPortrait({ driver, className = "", eager = false }) {
    const [failed, setFailed] = useState(false);

    if (!driver.image || failed) {
        return (
            <div className={`${className} driver-portrait-placeholder`} aria-label={`${driver.name} placeholder portrait`}>
                <span>{driver.code}</span>
            </div>
        );
    }

    return (
        <img
            className={className}
            src={driver.image}
            alt={`${driver.name} driver portrait`}
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            onError={() => setFailed(true)}
        />
    );
}

function DriverSparkline({ values = [], color = "#E10600", compact = false, large = false }) {
    const points = useMemo(() => {
        const safeValues = values.length ? values : [12, 10, 11, 9, 8];
        const max = Math.max(...safeValues, 1);
        const min = Math.min(...safeValues, max);
        const width = large ? 320 : compact ? 92 : 148;
        const height = large ? 86 : compact ? 34 : 48;
        const range = Math.max(max - min, 1);
        return safeValues
            .map((value, index) => {
                const x = (index / Math.max(safeValues.length - 1, 1)) * width;
                const y = height - ((value - min) / range) * (height - 10) - 5;
                return `${x.toFixed(1)},${y.toFixed(1)}`;
            })
            .join(" ");
    }, [values, compact, large]);

    const viewBox = large ? "0 0 320 86" : compact ? "0 0 92 34" : "0 0 148 48";

    return (
        <svg className={`driver-sparkline ${compact ? "is-compact" : ""} ${large ? "is-large" : ""}`} viewBox={viewBox} aria-hidden>
            <polyline className="driver-sparkline-glow" points={points} fill="none" stroke={color} />
            <polyline points={points} fill="none" stroke={color} />
        </svg>
    );
}

function StatChip({ label, value, accent = false }) {
    return (
        <span className={`driver-stat-chip ${accent ? "is-accent" : ""}`}>
            <small>{label}</small>
            <strong>{value}</strong>
        </span>
    );
}

function ModalStat({ label, value }) {
    return (
        <motion.div
            className="driver-modal-stat"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.32 }}
        >
            <span>{label}</span>
            <strong>{value ?? "—"}</strong>
        </motion.div>
    );
}

function StatBar({ label, value, color }) {
    const clamped = Math.max(0, Math.min(Number(value || 0), 100));
    return (
        <div className="driver-stat-bar">
            <div>
                <span>{label}</span>
                <strong>{Math.round(clamped)}%</strong>
            </div>
            <div className="driver-stat-bar-track">
                <motion.span
                    initial={{ width: 0 }}
                    animate={{ width: `${clamped}%` }}
                    transition={{ duration: 0.75, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
                    style={{ background: `linear-gradient(90deg, ${color}, #ffffff)` }}
                />
            </div>
        </div>
    );
}

function PanelTitle({ icon: Icon, title }) {
    return (
        <div className="driver-panel-title">
            <Icon className="h-4 w-4" />
            <h3>{title}</h3>
        </div>
    );
}

function DriversSkeleton() {
    return (
        <div className="drivers-skeleton">
            <div className="drivers-skeleton-hero shimmer" />
            <div className="drivers-skeleton-grid">
                {Array.from({ length: 8 }).map((_, index) => (
                    <div key={index} className="drivers-skeleton-card shimmer" />
                ))}
            </div>
        </div>
    );
}

function normalizeDriver(driver, index) {
    const status = driver.status || driver.active_status || "active";
    const firstName = driver.first_name || "";
    const lastName = driver.last_name || "";
    const name = driver.full_name || driver.display_name || `${firstName} ${lastName}`.trim() || "Unknown Driver";
    const rank = toNumber(driver.standing ?? driver.current_season_position);
    const code = driver.code || driver.abbreviation || makeDriverCode(name);
    const recentForm = Array.isArray(driver.recent_form)
        ? driver.recent_form.map((value) => Number(value)).filter((value) => Number.isFinite(value))
        : [];
    const formIndex = calculateFormIndex(recentForm);

    const normalized = {
        ...driver,
        id: driver.id || driver.driver_id || driver.slug || name.toLowerCase().replace(/\s+/g, "-"),
        firstName,
        lastName,
        name,
        code,
        status,
        rank,
        rankLabel: rank || index + 1,
        teamName: driver.team_name || driver.team_id || "No team listed",
        teamColor: driver.team_color || driver.primary_color || "#E10600",
        country: driver.country,
        number: driver.number,
        points: toNumber(driver.points),
        wins: toNumber(driver.wins),
        podiums: toNumber(driver.podiums),
        poles: toNumber(driver.poles),
        fastestLaps: toNumber(driver.fastest_laps),
        dnfs: toNumber(driver.dnfs),
        championships: toNumber(driver.championships) || 0,
        careerWins: toNumber(driver.career_wins ?? driver.wins),
        careerPodiums: toNumber(driver.career_podiums ?? driver.podiums),
        careerPoles: toNumber(driver.career_poles ?? driver.poles),
        qualifyingRank: toNumber(driver.qualifying_rank),
        racePaceRank: toNumber(driver.race_pace_rank),
        recentForm,
        formIndex,
        consistency: calculateConsistency(recentForm),
        averageFinish: averageFinish(recentForm),
        sparklineValues: recentForm.length ? recentForm.map((value) => Math.max(1, 24 - value)).reverse() : [],
        strengths: Array.isArray(driver.strengths) ? driver.strengths : [],
        bio: driver.bio,
    };

    return {
        ...normalized,
        image: getDriverImagePath(normalized),
    };
}

function sortDriversForLeaderboard(a, b) {
    const aRank = Number.isFinite(a.rank) ? a.rank : 999;
    const bRank = Number.isFinite(b.rank) ? b.rank : 999;
    if (aRank !== bRank) return aRank - bRank;
    if ((b.points || 0) !== (a.points || 0)) return (b.points || 0) - (a.points || 0);
    if ((b.championships || 0) !== (a.championships || 0)) return (b.championships || 0) - (a.championships || 0);
    if ((b.careerWins || 0) !== (a.careerWins || 0)) return (b.careerWins || 0) - (a.careerWins || 0);
    return a.name.localeCompare(b.name);
}

function makeDriverCode(name) {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(-3)
        .map((part) => part[0])
        .join("")
        .toUpperCase()
        .padEnd(3, "F")
        .slice(0, 3);
}

function calculateFormIndex(form = []) {
    if (!form.length) return null;
    const average = form.reduce((total, value) => total + value, 0) / form.length;
    return Math.max(1, Math.min(99, Math.round(100 - (average - 1) * 4.35)));
}

function calculateConsistency(form = []) {
    if (form.length < 2) return null;
    const average = form.reduce((total, value) => total + value, 0) / form.length;
    const variance = form.reduce((total, value) => total + Math.pow(value - average, 2), 0) / form.length;
    return Math.max(1, Math.min(99, Math.round(100 - Math.sqrt(variance) * 8)));
}

function averageFinish(form = []) {
    if (!form.length) return "N/A";
    const average = form.reduce((total, value) => total + value, 0) / form.length;
    return `P${average.toFixed(1)}`;
}

function percentile(value, max) {
    return Math.max(0, Math.min(100, (Number(value || 0) / max) * 100));
}

function toNumber(value) {
    if (value === null || value === undefined || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}
